import fs from 'fs-extra';
import { join } from 'path';
import { createHash } from 'crypto';
import chalk from 'chalk';
import ora from 'ora';
import type { CacheMeta } from './types';

/**
 * 缓存版本管理器
 * 负责管理缓存的版本控制、依赖追踪和失效检测
 */
export class CacheVersionManager {
  private versionDir: string;
  private dependencyGraph: Map<string, Set<string>> = new Map();
  private versionHistory: Map<string, string[]> = new Map();
  private spinner = ora();

  constructor(versionDir: string = '.synthia-cache/versions') {
    this.versionDir = versionDir;
    this.ensureVersionDir();
    this.loadDependencyGraph();
    this.loadVersionHistory();
  }

  /**
   * 计算文件或内容的哈希值
   */
  calculateHash(content: string | Buffer): string {
    const hash = createHash('sha256');
    hash.update(content);
    return hash.digest('hex');
  }

  /**
   * 计算文件哈希值
   */
  calculateFileHash(filePath: string): string {
    try {
      const content = fs.readFileSync(filePath, 'utf-8');
      return this.calculateHash(content);
    } catch (error) {
      console.warn(chalk.yellow(`无法读取文件 ${filePath}: ${error}`));
      return '';
    }
  }

  /**
   * 计算依赖文件的哈希值
   */
  calculateDependenciesHash(dependencies: string[]): string {
    const hashes = dependencies
      .map(dep => this.calculateFileHash(dep))
      .filter(Boolean);
    return this.calculateHash(hashes.join('|'));
  }

  /**
   * 检查缓存是否有效
   */
  isCacheValid(
    key: string,
    dependencies: string[],
    currentHash?: string
  ): boolean {
    const cacheMeta = this.getCacheMeta(key);
    if (!cacheMeta) {
      return false;
    }

    // 检查过期时间
    if (cacheMeta.expiresAt < Date.now()) {
      return false;
    }

    // 检查依赖是否变化
    const currentDepsHash = this.calculateDependenciesHash(dependencies);
    if (cacheMeta.dependenciesHash !== currentDepsHash) {
      return false;
    }

    // 如果提供了当前哈希值，检查是否匹配
    if (currentHash && cacheMeta.hash !== currentHash) {
      return false;
    }

    return true;
  }

  /**
   * 更新缓存版本信息
   */
  updateCacheVersion(
    key: string,
    value: any,
    dependencies: string[],
    tags: string[] = [],
    version: string = '1.0.0'
  ): CacheMeta {
    const hash = this.calculateHash(JSON.stringify(value));
    const dependenciesHash = this.calculateDependenciesHash(dependencies);
    const size = JSON.stringify(value).length;

    const meta: CacheMeta = {
      hash,
      size,
      expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7天
      dependencies,
      tags,
      version,
      dependenciesHash,
      createdAt: Date.now(),
      lastModified: Date.now(),
    };

    // 保存缓存元信息
    this.saveCacheMeta(key, meta);

    // 更新依赖图
    this.updateDependencyGraph(key, dependencies);

    // 更新版本历史
    this.updateVersionHistory(key, hash);

    return meta;
  }

  /**
   * 获取受影响的缓存键
   */
  getAffectedCacheKeys(changedFiles: string[]): string[] {
    const affectedKeys = new Set<string>();

    for (const file of changedFiles) {
      // 查找直接依赖此文件的缓存
      for (const [key, deps] of this.dependencyGraph.entries()) {
        if (deps.has(file)) {
          affectedKeys.add(key);
        }
      }
    }

    return Array.from(affectedKeys);
  }

  /**
   * 清理无效的缓存版本
   */
  cleanupInvalidVersions(): void {
    this.spinner.start('清理无效的缓存版本...');

    try {
      let cleanedCount = 0;
      const currentTime = Date.now();

      for (const [key, versions] of this.versionHistory.entries()) {
        const cacheMeta = this.getCacheMeta(key);
        if (!cacheMeta) {
          continue;
        }

        // 保留最新版本和当前使用的版本
        const validVersions = new Set([cacheMeta.hash]);
        const cleanedVersions = versions.filter(version => {
          if (validVersions.has(version)) {
            return true;
          }

          // 检查版本是否过期（超过30天）
          const versionPath = this.getVersionPath(key, version);
          if (fs.existsSync(versionPath)) {
            const stats = require('fs').statSync(versionPath);
            const age = currentTime - stats.mtime.getTime();
            if (age > 30 * 24 * 60 * 60 * 1000) {
              // 30天
              require('fs').unlinkSync(versionPath);
              cleanedCount++;
              return false;
            }
          }
          return true;
        });

        if (cleanedVersions.length !== versions.length) {
          this.versionHistory.set(key, cleanedVersions);
        }
      }

      this.saveVersionHistory();
      this.spinner.succeed(`清理完成，删除了 ${cleanedCount} 个无效版本`);
    } catch (error) {
      this.spinner.fail('清理无效版本失败');
      console.error(chalk.red('清理错误:'), error);
    }
  }

  /**
   * 获取缓存统计信息
   */
  getVersionStats(): {
    totalKeys: number;
    totalVersions: number;
    dependencyGraphSize: number;
    averageVersionsPerKey: number;
  } {
    const totalKeys = this.versionHistory.size;
    const totalVersions = Array.from(this.versionHistory.values()).reduce(
      (sum, versions) => sum + versions.length,
      0
    );
    const dependencyGraphSize = this.dependencyGraph.size;
    const averageVersionsPerKey = totalKeys > 0 ? totalVersions / totalKeys : 0;

    return {
      totalKeys,
      totalVersions,
      dependencyGraphSize,
      averageVersionsPerKey,
    };
  }

  /**
   * 导出版本信息
   */
  exportVersionInfo(): Buffer {
    const versionInfo = {
      dependencyGraph: Object.fromEntries(this.dependencyGraph),
      versionHistory: Object.fromEntries(this.versionHistory),
      timestamp: Date.now(),
    };

    return Buffer.from(JSON.stringify(versionInfo, null, 2));
  }

  /**
   * 导入版本信息
   */
  importVersionInfo(data: Buffer): void {
    try {
      const versionInfo = JSON.parse(data.toString());

      this.dependencyGraph = new Map(
        Object.entries(versionInfo.dependencyGraph)
      );
      this.versionHistory = new Map(Object.entries(versionInfo.versionHistory));

      this.saveDependencyGraph();
      this.saveVersionHistory();

      console.log(chalk.green('版本信息导入成功'));
    } catch (error) {
      console.error(chalk.red('版本信息导入失败:'), error);
      throw error;
    }
  }

  /**
   * 获取缓存元信息
   */
  private getCacheMeta(key: string): CacheMeta | null {
    const metaPath = join(this.versionDir, `${key}.meta.json`);
    if (!fs.existsSync(metaPath)) {
      return null;
    }

    try {
      return JSON.parse(fs.readFileSync(metaPath, 'utf-8'));
    } catch (error) {
      return null;
    }
  }

  /**
   * 保存缓存元信息
   */
  private saveCacheMeta(key: string, meta: CacheMeta): void {
    const metaPath = join(this.versionDir, `${key}.meta.json`);
    fs.writeFileSync(metaPath, JSON.stringify(meta, null, 2));
  }

  /**
   * 更新依赖图
   */
  private updateDependencyGraph(key: string, dependencies: string[]): void {
    this.dependencyGraph.set(key, new Set(dependencies));
    this.saveDependencyGraph();
  }

  /**
   * 更新版本历史
   */
  private updateVersionHistory(key: string, hash: string): void {
    const versions = this.versionHistory.get(key) || [];
    if (!versions.includes(hash)) {
      versions.push(hash);
      this.versionHistory.set(key, versions);
      this.saveVersionHistory();
    }
  }

  /**
   * 获取版本路径
   */
  private getVersionPath(key: string, version: string): string {
    return join(this.versionDir, `${key}.${version}.cache`);
  }

  /**
   * 确保版本目录存在
   */
  private ensureVersionDir(): void {
    if (!fs.existsSync(this.versionDir)) {
      fs.mkdirSync(this.versionDir, { recursive: true });
    }
  }

  /**
   * 加载依赖图
   */
  private loadDependencyGraph(): void {
    const graphPath = join(this.versionDir, 'dependency-graph.json');
    if (fs.existsSync(graphPath)) {
      try {
        const graphData = JSON.parse(fs.readFileSync(graphPath, 'utf-8'));
        this.dependencyGraph = new Map(Object.entries(graphData));
      } catch (error) {
        console.warn(chalk.yellow('无法加载依赖图，将重新创建'));
      }
    }
  }

  /**
   * 保存依赖图
   */
  private saveDependencyGraph(): void {
    const graphPath = join(this.versionDir, 'dependency-graph.json');
    const graphData = Object.fromEntries(this.dependencyGraph);
    fs.writeFileSync(graphPath, JSON.stringify(graphData, null, 2));
  }

  /**
   * 加载版本历史
   */
  private loadVersionHistory(): void {
    const historyPath = join(this.versionDir, 'version-history.json');
    if (fs.existsSync(historyPath)) {
      try {
        const historyData = JSON.parse(fs.readFileSync(historyPath, 'utf-8'));
        this.versionHistory = new Map(Object.entries(historyData));
      } catch (error) {
        console.warn(chalk.yellow('无法加载版本历史，将重新创建'));
      }
    }
  }

  /**
   * 保存版本历史
   */
  private saveVersionHistory(): void {
    const historyPath = join(this.versionDir, 'version-history.json');
    const historyData = Object.fromEntries(this.versionHistory);
    fs.writeFileSync(historyPath, JSON.stringify(historyData, null, 2));
  }
}
